import { useEffect, useRef, useState } from "react";
import type { QuishingConfig } from "../scenario/types";
import { audio } from "../lib/audio";

type Stage = "scan" | "permissions" | "capture";

/**
 * The phone the learner uses through the quishing attack, driven by the beat
 * `stage`:
 *   scan        -> point the camera at the menu QR; a sheet offers to open a
 *                  TYPOSQUAT URL (restaurant-lu1gi.com). Tapping Open is the bite.
 *   permissions -> the look-alike site over-asks for Camera / Microphone /
 *                  Location; each "Allow" is a disclosure.
 *   capture     -> the learner scrolls the menu while a "hacker capture" panel
 *                  shows the front camera, microphone (the live Giovanni+Maria
 *                  TTS conversation) and GPS being harvested in real time.
 */
export function QuishingPhone({
  config,
  stage,
  onGrant,
  onOpenSite,
  onDone,
}: {
  config: QuishingConfig;
  stage: Stage;
  onGrant: (perm: "camera" | "microphone" | "location") => void;
  onOpenSite: () => void;
  onDone: () => void;
}) {
  return (
    <div className="quish">
      <div className="quish-stagewrap">
        {stage === "scan" && <ScanStage config={config} onOpenSite={onOpenSite} onDone={onDone} />}
        {stage === "permissions" && <PermStage config={config} onGrant={onGrant} onDone={onDone} />}
        {stage === "capture" && <CaptureStage config={config} onDone={onDone} />}
      </div>
    </div>
  );
}

function PhoneFrame({ clock, children }: { clock?: string; children: React.ReactNode }) {
  return (
    <div className="quish-phone">
      <div className="quish-notch" />
      <div className="quish-statusbar">
        <span>{clock ?? "12:48"}</span>
        <span className="quish-sb-icons">
          <span>{"\u{1F4F6}"}</span>
          <span>{"\u{1F50B}"}</span>
        </span>
      </div>
      <div className="quish-screen">{children}</div>
    </div>
  );
}

/** Stage 1 — camera viewfinder over the QR, then the "open website?" sheet. */
function ScanStage({
  config,
  onOpenSite,
  onDone,
}: {
  config: QuishingConfig;
  onOpenSite: () => void;
  onDone: () => void;
}) {
  const [scanned, setScanned] = useState(false);
  const [opening, setOpening] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // (Re)arm QR detection whenever the viewfinder is live and nothing has been
  // opened yet. Tapping "Cancel" on the open-site sheet resets `scanned`, so
  // this re-detects the QR after a beat and re-offers the sheet — the camera is
  // still pointed at the code. Without it, Cancel was a dead end: the original
  // one-shot timer never re-fired, so the learner could never continue.
  useEffect(() => {
    if (scanned || opening) return;
    const t = window.setTimeout(() => mounted.current && setScanned(true), 1700);
    return () => window.clearTimeout(t);
  }, [scanned, opening]);

  const open = () => {
    onOpenSite();
    setOpening(true);
    window.setTimeout(() => mounted.current && onDone(), 1300);
  };

  return (
    <PhoneFrame clock={config.lockClock}>
      <div className="quish-cam">
        {/* viewfinder of the table + QR */}
        <div className="quish-cam-feed">
          <div className="quish-qr" aria-hidden>
            <QrGlyph />
          </div>
          <div className={`quish-reticle ${scanned ? "locked" : ""}`}>
            <span className="r-tl" />
            <span className="r-tr" />
            <span className="r-bl" />
            <span className="r-br" />
            {!scanned && <span className="quish-scanbar" />}
          </div>
          <div className="quish-cam-hint">{scanned ? "QR code detected" : "Point at the QR code…"}</div>
        </div>

        {dismissed && !scanned && !opening && (
          <div className="quish-cam-note">
            Good instinct to pause — but a polished code is hard to resist, and curiosity usually wins. Re-scanning…
          </div>
        )}

        {opening && <div className="quish-loading">Opening site…</div>}

        {scanned && !opening && (
          <div className="quish-sheet">
            <div className="quish-sheet-h">Open this website?</div>
            <div className="quish-url-big">{config.openPrompt.host}</div>
            <div className="quish-url-full">{config.openPrompt.full}</div>
            <div className="quish-sheet-row">
              <button
                className="quish-sbtn ghost"
                onClick={() => {
                  setDismissed(true);
                  setScanned(false);
                }}
              >
                Cancel
              </button>
              <button className="quish-sbtn primary" onClick={open}>
                Open
              </button>
            </div>
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}

/** Stage 2 — the look-alike site over-asks for permissions. */
function PermStage({
  config,
  onGrant,
  onDone,
}: {
  config: QuishingConfig;
  onGrant: (perm: "camera" | "microphone" | "location") => void;
  onDone: () => void;
}) {
  const perms = config.permissions;
  const [idx, setIdx] = useState(0);
  const [nudge, setNudge] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const cur = perms[idx];
  const allow = () => {
    if (!cur) return;
    onGrant(cur.id);
    if (idx + 1 < perms.length) {
      setNudge(false);
      setIdx(idx + 1);
    } else {
      window.setTimeout(() => mounted.current && onDone(), 900);
      setIdx(perms.length);
    }
  };

  return (
    <PhoneFrame clock={config.lockClock}>
      <div className="quish-browser">
        <div className="quish-addr">
          <span className="quish-lock">{"\u{1F512}"}</span>
          <span className="quish-addr-url">{config.openPrompt.host}</span>
        </div>
        <div className="quish-site-head">
          <div className="quish-site-logo">{config.menu.brand.slice(0, 1)}</div>
          <div className="quish-site-name">{config.menu.brand}</div>
          <div className="quish-site-sub">Loading digital menu…</div>
        </div>
        <div className="quish-skeleton">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="quish-skel-row" />
          ))}
        </div>

        {idx < perms.length && cur && (
          <div className="quish-permwrap">
            <div className="quish-perm">
              <div className="quish-perm-ico" aria-hidden>
                {cur.id === "camera" ? "\u{1F4F7}" : cur.id === "microphone" ? "\u{1F3A4}" : "\u{1F4CD}"}
              </div>
              <div className="quish-perm-title">{cur.title}</div>
              <div className="quish-perm-body">{cur.body}</div>
              {nudge && <div className="quish-perm-nudge">The menu won&apos;t load unless you allow this.</div>}
              <div className="quish-perm-row">
                <button className="quish-pbtn block" onClick={() => setNudge(true)}>
                  Don&apos;t allow
                </button>
                <button className="quish-pbtn allow" onClick={allow}>
                  {cur.allowLabel ?? "Allow"}
                </button>
              </div>
              <div className="quish-perm-dots">
                {perms.map((_, i) => (
                  <span key={i} className={i <= idx ? "on" : ""} />
                ))}
              </div>
            </div>
          </div>
        )}
        {idx >= perms.length && <div className="quish-loading">Opening menu…</div>}
      </div>
    </PhoneFrame>
  );
}

/** Stage 3 — browse the menu while the attacker harvests camera/mic/GPS. */
function CaptureStage({ config, onDone }: { config: QuishingConfig; onDone: () => void }) {
  const scroller = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const [shots, setShots] = useState(1);
  const [convo, setConvo] = useState<number>(0); // how many lines captured so far
  const [finished, setFinished] = useState(false);

  // auto-scroll the menu + periodic stills. No shutter sound: the page grabs
  // frames silently, the victim hears nothing.
  useEffect(() => {
    mounted.current = true;
    const scrollT = window.setInterval(() => {
      const el = scroller.current;
      if (el) el.scrollTop = Math.min(el.scrollHeight, el.scrollTop + 14);
    }, 60);
    const shutterT = window.setInterval(() => {
      if (!mounted.current) return;
      setShots((s) => s + 1);
    }, 2200);
    return () => {
      mounted.current = false;
      window.clearInterval(scrollT);
      window.clearInterval(shutterT);
    };
  }, []);

  // Play the family conversation sequentially. Await TTS prefetch BEFORE
  // revealing each bubble so the voice and transcript start together
  // (showing the line then awaiting fetch caused a 1–3s lag).
  useEffect(() => {
    let cancelled = false;
    const lang = config.language || "en-US";
    const voiceFor = (line: (typeof config.conversation)[number]) =>
      line.voiceId || (line.speaker === "maria" ? "shimmer" : "onyx");
    const instrFor = (line: (typeof config.conversation)[number]) =>
      line.speaker === "maria"
        ? "Speak as a warm, relaxed older Italian woman chatting over lunch."
        : "Speak as a cheerful young Italian man chatting with his mum over lunch.";

    (async () => {
      // Kick off all fetches in parallel; first line is awaited below.
      void Promise.all(
        config.conversation.map((line) =>
          audio.prefetchSpeech(line.text, {
            voice: voiceFor(line),
            lang,
            instructions: instrFor(line),
          })
        )
      );
      for (let i = 0; i < config.conversation.length; i++) {
        if (cancelled || !mounted.current) return;
        const line = config.conversation[i];
        const opts = { voice: voiceFor(line), lang, instructions: instrFor(line) };
        await audio.prefetchSpeech(line.text, opts);
        if (cancelled || !mounted.current) return;
        await audio.speak(line.text, {
          ...opts,
          onStart: () => {
            if (!cancelled && mounted.current) setConvo(i + 1);
          },
        });
        if (cancelled || !mounted.current) return;
        await new Promise((r) => window.setTimeout(r, 180));
      }
      if (mounted.current) setFinished(true);
    })();
    return () => {
      cancelled = true;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const captureItems = config.captureItems ?? ["Front camera", "Microphone", "GPS location"];

  return (
    <div className="quish-capture">
      <PhoneFrame clock={config.lockClock}>
        <div className="quish-browser">
          <div className="quish-addr">
            <span className="quish-lock">{"\u{1F512}"}</span>
            <span className="quish-addr-url">{config.openPrompt.host}</span>
          </div>
          <div className="quish-menu" ref={scroller}>
            <div className="quish-menu-hero">
              <div className="quish-menu-brand">{config.menu.brand}</div>
              <div className="quish-menu-tag">Digital Menu</div>
            </div>
            {config.menu.sections.map((sec, si) => (
              <div key={si} className="quish-menu-sec">
                <div className="quish-menu-sec-h">{sec.name}</div>
                {sec.items.map((it, ii) => (
                  <div key={ii} className="quish-menu-item">
                    <div className="quish-menu-item-main">
                      <div className="quish-menu-item-name">{it.name}</div>
                      {it.desc && <div className="quish-menu-item-desc">{it.desc}</div>}
                    </div>
                    <div className="quish-menu-item-price">{it.price}</div>
                  </div>
                ))}
              </div>
            ))}
            <div className="quish-menu-foot">Buon appetito!</div>
          </div>
        </div>
      </PhoneFrame>

      {/* the attacker's live capture panel */}
      <div className="quish-hack">
        <div className="quish-hack-h">
          <span className="quish-rec" /> LIVE CAPTURE · attacker view
        </div>

        <div className="quish-hack-grid">
          <div className="quish-hack-card cam">
            <div className="quish-hack-card-h">{"\u{1F4F7}"} {captureItems[0]}</div>
            <div className="quish-hack-photo">
              {config.capturePhotoUrl ? <img src={config.capturePhotoUrl} alt="" /> : <div className="quish-hack-ph" />}
              <span className="quish-hack-grain" />
              <span className="quish-hack-rec-dot" />
              <span key={shots} className="quish-hack-flash" />
            </div>
            <div className="quish-hack-meta">{shots} {shots === 1 ? "still" : "stills"} grabbed</div>
          </div>

          <div className="quish-hack-card loc">
            <div className="quish-hack-card-h">{"\u{1F4CD}"} {captureItems[2]}</div>
            <div className="quish-hack-map">
              <MapGlyph />
              <span className="quish-hack-pinwrap">
                <span className="quish-hack-pin">{"\u{1F4CC}"}</span>
              </span>
              <span className="quish-hack-coords">45.4642, 9.1900 · ±5 m</span>
            </div>
            <div className="quish-hack-meta">Ristorante Luigi · Via Roma 14</div>
          </div>
        </div>

        <div className="quish-hack-card mic">
          <div className="quish-hack-card-h">
            {"\u{1F3A4}"} {captureItems[1]} — transcribing
          </div>
          <div className="quish-hack-convo">
            {config.conversation.slice(0, convo).map((l, i) => (
              <div key={i} className={`quish-bubble ${l.speaker}`}>
                <span className="quish-bubble-name">{l.name}</span>
                {l.text}
              </div>
            ))}
            {!finished && convo > 0 && <div className="quish-bubble typing">…</div>}
          </div>
        </div>

        <button className="btn quish-continue" disabled={!finished} onClick={() => onDone()}>
          {finished ? "See what just happened" : "Listening…"}
        </button>
      </div>
    </div>
  );
}

/** Small street map behind the GPS pin (decorative, fictional streets). */
function MapGlyph() {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden>
      <rect width="100" height="100" fill="#13241b" />
      <path d="M-5 80 C 20 72, 35 90, 60 82 S 95 72, 105 78 L 105 105 L -5 105 Z" fill="#163a52" />
      <rect x="61" y="4" width="35" height="19" rx="2" fill="#1f4d2e" />
      <g fill="#1c3326">
        <rect x="4" y="4" width="22" height="19" rx="1.5" />
        <rect x="32" y="4" width="23" height="19" rx="1.5" />
        <rect x="4" y="29" width="22" height="15" rx="1.5" />
        <rect x="32" y="29" width="23" height="15" rx="1.5" />
        <rect x="61" y="29" width="35" height="15" rx="1.5" />
        <rect x="4" y="50" width="22" height="6" rx="1.5" />
        <rect x="32" y="50" width="23" height="6" rx="1.5" />
        <rect x="61" y="50" width="35" height="6" rx="1.5" />
        <rect x="4" y="62" width="22" height="10" rx="1.5" />
        <rect x="32" y="62" width="23" height="10" rx="1.5" />
        <rect x="61" y="62" width="35" height="10" rx="1.5" />
      </g>
      <g stroke="#2f5a40" strokeWidth="2.4" fill="none" strokeLinecap="round">
        <path d="M29 0 V 76" />
        <path d="M58 0 V 76" />
        <path d="M0 26 H 100" />
        <path d="M0 59 H 100" />
      </g>
      <path d="M0 47 H 100" stroke="#4f8a64" strokeWidth="4.2" />
      <text x="5" y="48.4" fontSize="3.4" fill="#e6f5ea" fontFamily="system-ui, sans-serif" fontWeight="600">Via Roma</text>
      <circle cx="51" cy="47" r="10" fill="rgba(255,82,82,.16)" stroke="rgba(255,96,96,.55)" strokeWidth=".8" />
      <circle cx="51" cy="47" r="1.8" fill="#ff5a5a" />
    </svg>
  );
}

/** A small CSS/SVG QR glyph for the viewfinder (decorative). */
function QrGlyph() {
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden>
      <rect x="0" y="0" width="100" height="100" fill="#fff" />
      <g fill="#111">
        <rect x="6" y="6" width="22" height="22" />
        <rect x="72" y="6" width="22" height="22" />
        <rect x="6" y="72" width="22" height="22" />
        <rect x="12" y="12" width="10" height="10" fill="#fff" />
        <rect x="78" y="12" width="10" height="10" fill="#fff" />
        <rect x="12" y="78" width="10" height="10" fill="#fff" />
        {Array.from({ length: 11 }).map((_, r) =>
          Array.from({ length: 11 }).map((_, c) => {
            if ((r < 3 && c < 3) || (r < 3 && c > 7) || (r > 7 && c < 3)) return null;
            return (r * 7 + c * 3) % 5 < 2 ? (
              <rect key={`${r}-${c}`} x={6 + c * 8} y={6 + r * 8} width="6" height="6" />
            ) : null;
          })
        )}
      </g>
    </svg>
  );
}
